import { useState } from "react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { KeyRound, Save, Eye, EyeOff } from "lucide-react";

/**
 * Change-password card for the Dashboard "Security" section. Calls
 * POST /api/auth/change-password with the current + new password. Validates
 * client-side that new password is at least 8 chars and differs from current.
 */
export default function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const canSubmit =
    current.length >= 1 &&
    next.length >= 8 &&
    confirm === next &&
    next !== current;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await http.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password changed. Use it next time you sign in.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not change password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="card-elev p-6"
      data-testid="change-password-card"
      aria-labelledby="change-password-heading"
    >
      <div className="flex items-center gap-2">
        <KeyRound className="w-4 h-4 text-neon-cyan" />
        <h2 id="change-password-heading" className="text-xl font-semibold">
          Change password
        </h2>
      </div>
      <p className="mt-1 text-sm text-[var(--fg-muted)]">
        Use at least 8 characters. Mix letters, numbers and symbols for strength.
      </p>

      <form onSubmit={submit} className="mt-4 grid gap-3 sm:max-w-md" noValidate>
        <label className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">
          Current password
          <input
            data-testid="cp-current"
            type={show ? "text" : "password"}
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="input-lux mt-1 normal-case tracking-normal"
            autoComplete="current-password"
          />
        </label>

        <label className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">
          New password
          <input
            data-testid="cp-new"
            type={show ? "text" : "password"}
            required
            minLength={8}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="input-lux mt-1 normal-case tracking-normal"
            autoComplete="new-password"
          />
        </label>

        <label className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">
          Confirm new password
          <input
            data-testid="cp-confirm"
            type={show ? "text" : "password"}
            required
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="input-lux mt-1 normal-case tracking-normal"
            autoComplete="new-password"
          />
        </label>

        {confirm && confirm !== next && (
          <div className="text-sm text-red-400" data-testid="cp-mismatch">
            Passwords don't match.
          </div>
        )}
        {next && current && next === current && (
          <div className="text-sm text-amber-300" data-testid="cp-same">
            New password must differ from your current password.
          </div>
        )}

        <div className="flex items-center justify-between gap-3 pt-1">
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="btn-ghost text-xs"
            data-testid="cp-toggle-visibility"
            aria-pressed={show}
          >
            {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {show ? "Hide" : "Show"} passwords
          </button>
          <button
            type="submit"
            disabled={!canSubmit || busy}
            className="btn-primary"
            data-testid="cp-submit"
          >
            <Save className="w-4 h-4" />
            {busy ? "Saving…" : "Update password"}
          </button>
        </div>
      </form>
    </section>
  );
}
